import { useState, useEffect, useContext } from 'react'
import { useRouter } from 'next/router'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import FilterDropdownWrapper from 'common/FilterDropdownWrapper'
import { modifyURLQuery } from 'utils/common'
import { MobileFilterModalContext } from 'context/ProviderSearch/MobileFilterModalProvider'
import useBreakpoint from 'hooks/useBreakPoint'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import { getFilterIdFromSalesforceField } from 'utils/salesforce'
import useHandleSalesforceLeadUpdate from 'hooks/salesforce/useHandleSalesforceLeadUpdate'
import useNewGetUserTreatment from 'hooks/useNewGetUserTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { If } from '@/utility_components/ControlStatements'
import CounselingGridDropdown from './CounselingGridDropdown'
import { getSortedCounselingTypes } from 'app/ProviderSearch/utils'
import { getServiceTypeDisplayLabel } from './constants'

type CounselingFilterProps = {
  allCounselingTypes: ProviderFilter[]
}

const CounselingFilter: React.FC<CounselingFilterProps> = ({
  allCounselingTypes,
}) => {
  const router = useRouter()
  const { query } = router
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [selectedCounselingType, setSelectedCounselingType] = useState<string>(
    ''
  )
  const { openMobileFilter } = useContext(MobileFilterModalContext)
  const { isMobileScreen, isLargeSize } = useBreakpoint()

  const handleLeadUpdate = useHandleSalesforceLeadUpdate()
  const treatment = useNewGetUserTreatment(
    TreatmentFlags.SALESFORCE_COUNSELING_TYPE_AUTOFILL
  )
  const autoFillCounselingType =
    treatment.treatmentValue === treatmentFlagValues.ON

  useEffect(() => {
    const counselingTypeIdInQuery = _pick(query, 'counselingType[]')[
      'counselingType[]'
    ]

    if (!counselingTypeIdInQuery && !selectedCounselingType) {
      return
    }

    if (!counselingTypeIdInQuery && selectedCounselingType) {
      setSelectedCounselingType('')
      return
    }

    const counselingType = allCounselingTypes.find(
      (counselingType) => counselingType.id === counselingTypeIdInQuery
    )?.value

    setSelectedCounselingType(counselingType)
  }, [query])

  const onCounselingSelect = (option: SelectItem) => {
    const selectedOption = allCounselingTypes.find(
      (counselingType) => counselingType.id === option.value
    )

    modifyURLQuery(router, {
      counselingType: [selectedOption.id],
      offset: '0',
    })
    setShowDropdown(false)
  }

  handleLeadUpdate(
    (results) => {
      if (!autoFillCounselingType) {
        return
      }

      const { lead } = results
      let selectedOption = allCounselingTypes.find(
        (counselingType) =>
          counselingType.id ===
          getFilterIdFromSalesforceField(lead.Service_type__c)
      )

      if (!selectedOption) {
        selectedOption = allCounselingTypes.find(
          (counselingType) =>
            counselingType.id ===
            getFilterIdFromSalesforceField('individualcounseling')
        )
      }

      if (!selectedOption) {
        return
      }

      onCounselingSelect({
        label: selectedOption.value,
        value: selectedOption.id,
      })
    },
    ['Service_type__c']
  )

  const clearSelectedCounselingType = () => {
    const formattedQuery = _omit(query, ['counselingType[]'])
    modifyURLQuery(router, formattedQuery, true)

    if (showDropdown) {
      setShowDropdown(false)
    }
  }

  return (
    <FilterDropdownWrapper
      placeholder="Service type"
      value={getServiceTypeDisplayLabel(selectedCounselingType)}
      onPillClick={(shouldShow) => {
        if (isMobileScreen || isLargeSize) {
          openMobileFilter(ProviderFilterTypes.COUNSELING_TYPE)
        } else {
          setShowDropdown(shouldShow)
        }
      }}
      showDropdown={showDropdown}
      onClearValue={clearSelectedCounselingType}
      className="mr-2"
    >
      <If condition={showDropdown}>
        <CounselingGridDropdown
          options={getSortedCounselingTypes(allCounselingTypes).map(
            (counselingType) => ({
              label: counselingType.value,
              value: counselingType.id,
            })
          )}
          onSelectItem={onCounselingSelect}
          selectedValue={selectedCounselingType}
        />
      </If>
    </FilterDropdownWrapper>
  )
}

export default CounselingFilter
